import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { ToastService } from '@/app/core/service/toast.service';
import { Toast } from '@/app/core/models/toast.model';
import { CommonModule } from '@angular/common';
@Component({
  selector: 'app-toast-container',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="toast-container" [attr.data-position]="position">
      <div *ngFor="let t of toasts" class="toast" [ngClass]="t.type" (mouseenter)="pause(t.id)" (mouseleave)="resume(t.id)">
        <div class="toast-body">
          <div class="toast-message">{{ t.message }}</div>
          <div class="toast-actions">
            <button *ngIf="t.action" class="toast-action" (click)="onAction(t)">{{ t.action.label }}</button>
            <button class="toast-close" (click)="dismiss(t.id)">✕</button>
          </div>
        </div>
        <div class="toast-progress" [style.width.%]="progress[t.id] || 0"></div>
      </div>
    </div>
  `,
  styles: [`
    /* container */
    .toast-container {
      position: fixed;
      z-index: 9999;
      right: 16px;
      bottom: 16px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      pointer-events: none;
      max-width: 360px;
    }
    /* each toast */
    .toast {
      pointer-events: auto;
      background: #222;
      color: #fff;
      padding: 12px 12px;
      border-radius: 8px;
      box-shadow: 0 6px 20px rgba(0,0,0,0.15);
      transform-origin: right bottom;
      animation: toast-in .22s ease-out;
      overflow: hidden;
      position: relative;
    }
    .toast.success { background: #1f8a3e; }
    .toast.error { background: #b22222; }
    .toast.info { background: #1e88e5; }
    .toast.warning { background: #f39c12; color: #111; }

    .toast-body { display:flex; align-items:center; justify-content:space-between; gap: 8px; }
    .toast-message { flex:1; padding-right:8px; }
    .toast-actions { display:flex; gap:6px; align-items:center; }
    .toast-action { background:transparent; border:none; color:inherit; font-weight:600; cursor:pointer; }
    .toast-close { background:transparent; border:none; color:inherit; opacity:0.85; cursor:pointer; }

    .toast-progress {
      position:absolute;
      left:0;
      bottom:0;
      height:3px;
      background: rgba(255,255,255,0.25);
      transition: width 0.1s linear;
    }

    @keyframes toast-in {
      from { opacity: 0; transform: translateY(6px) scale(.99); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }
  `]
})
export class ToastComponent implements OnInit, OnDestroy {
  toasts: Toast[] = [];
  private sub = new Subscription();
  // track progress percentage per toast id and timers
  progress: Record<string, number> = {};
  private timers: Record<string, { timerSub?: Subscription; remaining?: number; startedAt?: number }> = {};

  // optional: expose position
  position: 'bottom-right' | 'top-right' | 'bottom-center' = 'bottom-right';

  constructor(private toastService: ToastService) { }

  ngOnInit() {
    this.sub.add(this.toastService.toasts$.subscribe(list => {
      this.toasts = list;
      // start timers for new toasts
      list.forEach(t => {
        if (!this.timers[t.id]) this.startTimer(t);
      });
    }));
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    Object.values(this.timers).forEach(t => t.timerSub?.unsubscribe());
  }

  private startTimer(t: Toast) {
    const duration = t.duration ?? 4000;
    const id = t.id;
    this.progress[id] = 100;
    this.timers[id] = { remaining: duration, startedAt: Date.now() };

    // timer ticks every 100ms
    const tick$ = timer(0, 100);
    this.timers[id].timerSub = tick$.subscribe(() => {
      const elapsed = Date.now() - (this.timers[id].startedAt ?? Date.now());
      const rem = Math.max(0, (duration - elapsed));
      this.progress[id] = (rem / duration) * 100;
      if (rem <= 0) {
        this.dismiss(id);
      }
    });
  }

  pause(id: string) {
    const t = this.timers[id];
    if (!t || !t.timerSub) return;
    const elapsed = Date.now() - (t.startedAt ?? Date.now());
    t.remaining = Math.max(0, (t.remaining ?? 0) - elapsed);
    t.timerSub.unsubscribe();
    t.timerSub = undefined;
  }

  resume(id: string) {
    const t = this.timers[id];
    if (!t) return;
    t.startedAt = Date.now();
    // create a new timer using remaining
    const durationLeft = t.remaining ?? 0;
    const tick$ = timer(0, 100);
    t.timerSub = tick$.subscribe(() => {
      const elapsed = Date.now() - (t.startedAt ?? Date.now());
      const rem = Math.max(0, durationLeft - elapsed);
      const original = (this.toasts.find(x => x.id === id)?.duration ?? durationLeft) || durationLeft;
      this.progress[id] = (rem / original) * 100;
      if (rem <= 0) this.dismiss(id);
    });
  }

  dismiss(id: string) {
    this.toastService.remove(id);
    const t = this.timers[id];
    if (t?.timerSub) t.timerSub.unsubscribe();
    delete this.timers[id];
    delete this.progress[id];
  }

  onAction(t: Toast) {
    if (t.action?.callback) {
      try { t.action.callback(); } catch (e) { }
    }
    this.dismiss(t.id);
  }
}
